/**
 * usageLib.ts — hàm thuần cho các màn phân tích lượt dùng ưu đãi
 * (PromoDetailView + analytics/*). Không React, không mạng, không Date.now().
 *
 * Khoá mốc thời gian bám đúng thứ máy chủ phát ra (promo/analytics.go
 * bucketExprSQL): hour "08" · day "2026-08-23" · week (thứ Hai đầu tuần)
 * "2026-08-18" · month "2026-08" · quarter "2026-Q3" · year "2026".
 */
import { LEAD_SOURCES } from "@/features/orders/pipeline/types";

import type { PromoUsageBucket, PromoUsageBucketKind } from "./api";
import { labelOrderLeadSource } from "@/lib/orderUtils";

export type BucketOption = { value: PromoUsageBucketKind; label: string };

/** Thứ tự cố định giờ → năm — đúng 6 mức CEO chốt. */
export const BUCKET_OPTIONS: readonly BucketOption[] = [
  { value: "hour", label: "Giờ" },
  { value: "day", label: "Ngày" },
  { value: "week", label: "Tuần" },
  { value: "month", label: "Tháng" },
  { value: "quarter", label: "Quý" },
  { value: "year", label: "Năm" },
];

export function isUsageBucket(v: unknown): v is PromoUsageBucketKind {
  return typeof v === "string" && BUCKET_OPTIONS.some((o) => o.value === v);
}

/** Nhãn trục cho một khoá mốc. Khoá lạ/thiếu trả nguyên, không ném. */
export function bucketLabel(bucket: PromoUsageBucketKind, key: string): string {
  switch (bucket) {
    case "hour":
      return key ? `${key}h` : key;
    case "day":
    case "week": {
      const [, m, d] = key.split("-");
      if (!m || !d) return key;
      return bucket === "week" ? `Tuần ${d}/${m}` : `${d}/${m}`;
    }
    case "month": {
      const [y, m] = key.split("-");
      if (!y || !m) return key;
      return `T${Number(m)}/${y}`;
    }
    case "quarter": {
      const [y, q] = key.split("-");
      if (!y || !q) return key;
      return `${q}/${y}`;
    }
    case "year":
    default:
      return key;
  }
}

const HOURS: readonly string[] = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));

/** Trục giờ luôn phải đủ 24 cột — điền 0 vào giờ không có dữ liệu. */
export function fillHours(rows: PromoUsageBucket[]): PromoUsageBucket[] {
  const byHour = new Map(rows.map((r) => [r.bucket, r]));
  return HOURS.map(
    (h) => byHour.get(h) ?? { bucket: h, applied: 0, orders: 0, benefit: 0, revenue: 0 },
  );
}

const pctFmt = new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 1 });

/** null/undefined → "—" (máy chủ trả null khi mẫu rỗng). */
export function pctLabel(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${pctFmt.format(v)}%`;
}

/** phần / tổng × 100; tổng 0 → null để nơi gọi vẽ "—". */
export function shareOf(part: number, total: number): number | null {
  if (!total) return null;
  return (part / total) * 100;
}

const VN_TZ = "Asia/Ho_Chi_Minh";

/**
 * Ngày lịch VN dạng "YYYY-MM-DD".
 *
 * Ghép tay từ formatToParts thay vì để Intl tự ráp chuỗi: bộ dữ liệu ngôn ngữ
 * của Node đầy đủ hay rút gọn đều cho ra cùng một kết quả, nên nhãn không
 * đổi hình giữa máy dev, máy chạy bài kiểm và trình duyệt.
 */
function vnParts(iso: string): { y: string; m: string; d: string } | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: VN_TZ,
  }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const y = get("year");
  const m = get("month");
  const d = get("day");
  return y && m && d ? { y, m, d } : null;
}

/** "10/09" giờ VN. */
function ddmmVN(iso: string): string {
  const p = vnParts(iso);
  return p ? `${p.d}/${p.m}` : "";
}

/** Ngày lịch VN quy về số ngày, để trừ nhau bỏ qua giờ trong ngày. */
function vnDayNumber(iso: string): number {
  const p = vnParts(iso);
  if (!p) return Number.NaN;
  return Math.floor(Date.parse(`${p.y}-${p.m}-${p.d}T00:00:00Z`) / 86_400_000);
}

/** Nới lỏng có chủ đích: PromoUsageSummary vừa khít, mà {forecastExhaustAt} tay cũng vừa. */
export type ForecastInput = {
  maxUses?: number | null | undefined;
  usedCount?: number | null | undefined;
  forecastExhaustAt?: string | null | undefined;
};

/**
 * Câu dự báo dưới dòng "còn X/Y lượt".
 *   không có trần        → ""
 *   đã dùng hết          → "Đã hết lượt"
 *   chưa đủ nhịp         → "Không đủ dữ liệu để dự báo"
 *   dự báo trước hạn mã  → "Dự kiến hết lượt ~dd/mm (trước hạn N ngày)"
 *   còn lại              → "Dự kiến hết lượt ~dd/mm"
 */
export function forecastLabel(s: ForecastInput, validTo?: string | null): string {
  if (s.maxUses == null) return "";
  const used = s.usedCount ?? 0;
  if (used >= s.maxUses) return "Đã hết lượt";
  if (!s.forecastExhaustAt) return "Không đủ dữ liệu để dự báo";
  const day = ddmmVN(s.forecastExhaustAt);
  if (!day) return "Không đủ dữ liệu để dự báo";
  let out = `Dự kiến hết lượt ~${day}`;
  if (validTo) {
    const days = vnDayNumber(validTo) - vnDayNumber(s.forecastExhaustAt);
    if (days > 0) out += ` (trước hạn ${days} ngày)`;
  }
  return out;
}

const WEEKDAYS: Record<string, string> = {
  "1": "Thứ 2",
  "2": "Thứ 3",
  "3": "Thứ 4",
  "4": "Thứ 5",
  "5": "Thứ 6",
  "6": "Thứ 7",
  "7": "Chủ nhật",
};

/** Khoá thứ theo chuẩn ISO "1".."7" (byWeekday.key của máy chủ) → chữ Việt. */
export function weekdayLabel(key: string): string {
  return WEEKDAYS[key] ?? key;
}

const UNKNOWN = "(không rõ)";

const LEAD_SOURCE_LABEL = new Map(LEAD_SOURCES.map((s) => [s.value, s.label]));

export function leadSourceLabel(key: string): string {
  if (!key) return UNKNOWN;
  // LEAD_SOURCES là danh sách LỰA CHỌN của màn tạo lead, nên nguồn do máy
  // đóng dấu không nằm trong đó — phải hỏi tiếp bảng riêng, nếu không báo cáo
  // hiện trần chữ "public_booking".
  return LEAD_SOURCE_LABEL.get(key) ?? labelOrderLeadSource(key);
}
